import type { HydratedImage } from "../../generated/api/types.gen";
import { useNaturalImageSize } from "../../hooks/useNaturalImageSize";
import type { SelectionState } from "../../hooks/useRefineSelection";
import { isOutputAspectRatio } from "../../lib/image-constants";
import { ErrorToast } from "../ErrorToast";
import { LoadingSpinner } from "../LoadingSpinner";
import { ReplaceComponentModal } from "../ReplaceComponentModal";
import { SelectionOptionsMenu } from "./SelectionOptionsMenu";
import { SelectionOverlay } from "./SelectionOverlay";

/** Largest 3:2 frame that fits the canvas. Near-3:2 (generated) images
 *  cover so Gemini's 1264×848 doesn't letterbox; other ratios contain.
 *
 *  The root is split in two: an unclipped wrapper that the selection options
 *  menu can overhang, and the clipped frame that holds the image and marquee. */
export function RefineStage({
  image,
  isRegenerating,
  selection,
}: {
  image: HydratedImage;
  isRegenerating: boolean;
  selection: SelectionState;
}) {
  const natural = useNaturalImageSize(image.url);
  const fillsFrame = natural
    ? isOutputAspectRatio(natural.width, natural.height)
    : false;

  return (
    <div className="relative aspect-[3/2] w-[min(100cqw,calc(100cqh*3/2))]">
      <div className="absolute inset-0 overflow-hidden rounded-lg border border-[var(--stroke-field)] bg-[var(--bg-default)]">
        <img
          src={image.url}
          alt="Refined design"
          draggable={false}
          className={`absolute inset-0 h-full w-full ${fillsFrame ? "object-cover" : "object-contain"}`}
        />
        {selection.isActive && <SelectionOverlay marquee={selection.marquee} />}
        {(isRegenerating || selection.isCreating || selection.isReplacing) && (
          <div className="absolute inset-0 flex items-center justify-center bg-[var(--bg-default)] opacity-50">
            <LoadingSpinner
              text={
                selection.isCreating
                  ? "Creating design..."
                  : selection.isReplacing
                    ? "Replacing..."
                    : "Refining..."
              }
              size="lg"
            />
          </div>
        )}
      </div>
      {selection.openMenuAt &&
        selection.pending &&
        !selection.isCreating &&
        !selection.isReplacing &&
        !selection.isReplaceModalOpen && (
          <SelectionOptionsMenu
            anchor={selection.openMenuAt}
            frame={selection.pending.frame}
            onCreateNewDesign={selection.createNewDesign}
            onReplaceWithAsset={selection.openReplaceModal}
            onClose={selection.closeMenu}
            validationMessage={selection.pending.validationMessage}
          />
        )}
      <ErrorToast
        message={selection.error}
        onDismiss={selection.dismissError}
      />
      {selection.isReplaceModalOpen && (
        <ReplaceComponentModal
          isOpen
          projectId={image.project_id}
          onSelect={selection.replaceWithAsset}
          onClose={selection.closeReplaceModal}
        />
      )}
    </div>
  );
}
